import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Users } from 'lucide-react';

import type { UpdateUserBody, User } from '@tasks/contracts';

import { resendInvitation, revokeInvitation } from '../../api/invitations';
import { listUsers, setUserActive, updateUser } from '../../api/users';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Page, PageHeader } from '../../components/Page';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Select, type SelectOption } from '../../components/Select';
import { Skeleton } from '../../components/Skeleton';
import { Table } from '../../components/Table';
import { useToast } from '../../components/Toast';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { humanizeError } from '../../lib/errors';
import type { GlobalRole } from '../../lib/types';
import { globalRoleLabels } from '../../i18n/enums';
import { InviteUserModal } from './InviteUserModal';
import { UserAvatarEditor } from './UserAvatarEditor';
import { useUserColumns } from './useUserColumns';
import styles from './AdminPages.module.css';

const PAGE_SIZE = 50;

const ROLE_OPTIONS: readonly SelectOption<GlobalRole>[] = (['admin', 'member'] as const).map((value) => ({
  value,
  label: globalRoleLabels[value],
}));

type UserFormState = {
  readonly login: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: GlobalRole;
  readonly password: string;
};

const EMPTY_FORM: UserFormState = {
  login: '',
  email: '',
  displayName: '',
  role: 'member',
  password: '',
};

/**
 * `/admin/users` — таблица пользователей, приглашение по email, правка,
 * активация (UC-ADM-*, UC-USR-06, UC-USR-07).
 */
export function UsersPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 300);
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<User | null>(null);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<UserFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<User | null>(null);

  useEffect(() => {
    setOffset(0);
  }, [query]);

  const usersQuery = useQuery({
    queryKey: queryKeys.admin.users(query, 'all', offset),
    queryFn: ({ signal }) => listUsers({ q: query, status: 'all', limit: PAGE_SIZE, offset }, signal),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.admin.users(query, 'all', offset) });
  };

  const onError = (cause: unknown) => {
    const human = humanizeError(cause);
    setFormError(human.message);
    toast.error(human.message, { code: human.code });
  };

  const updateMutation = useMutation({
    mutationFn: (variables: { readonly id: string; readonly body: UpdateUserBody }) =>
      updateUser(variables.id, variables.body),
    onSuccess: () => {
      setEditing(null);
      setFormError(null);
      invalidate();
    },
    onError,
  });

  const activeMutation = useMutation({
    mutationFn: (variables: { readonly id: string; readonly isActive: boolean }) =>
      setUserActive(variables.id, variables.isActive),
    onSuccess: () => invalidate(),
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      toast.error(human.message, { code: human.code });
    },
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => resendInvitation(id),
    onSuccess: (invitation) => {
      invalidate();
      toast.success(`Приглашение отправлено повторно на ${invitation.email}`);
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      toast.error(human.message, { code: human.code });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeInvitation(id),
    onSuccess: () => {
      setRevoking(null);
      invalidate();
      toast.success('Приглашение отозвано');
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      toast.error(human.message, { code: human.code });
    },
  });

  const onEditUser = useCallback((user: User) => {
    setFormError(null);
    setForm({
      ...EMPTY_FORM,
      login: user.login,
      email: user.email ?? '',
      displayName: user.displayName,
      role: user.role,
    });
    setEditing(user);
  }, []);

  // У агента меняется специализация и доступ к серверу, у человека — почта и роль.
  const isAgent = editing?.kind === 'ai_agent';

  const columns = useUserColumns({
    onEdit: onEditUser,
    onToggleActive: useCallback(
      (variables: { readonly id: string; readonly isActive: boolean }) => activeMutation.mutate(variables),
      [activeMutation],
    ),
    onResend: useCallback((id: string) => resendMutation.mutate(id), [resendMutation]),
    onRevoke: useCallback((id: string) => {
      const target = usersQuery.data?.items.find((item) => item.invitationId === id);
      setRevoking(target ?? null);
    }, [usersQuery.data, usersQuery.data?.items]),
    isInvitationPending: resendMutation.isPending || revokeMutation.isPending,
  });

  const total = usersQuery.data?.total ?? 0;

  return (
    <Page width="wide">
      <PageHeader
        title="Пользователи"
        actions={
          <Button
            variant="primary"
            icon={<UserPlus size={14} />}
            onClick={() => {
              setForm(EMPTY_FORM);
              setFormError(null);
              setCreateOpen(true);
            }}
          >
            Пригласить
          </Button>
        }
      />

      <div className={styles['toolbar']}>
        <div className={styles['searchBox']}>
          <Input
            label="Поиск"
            placeholder="Логин, имя или email"
            value={rawQuery}
            onChange={(event) => setRawQuery(event.target.value)}
          />
        </div>
      </div>

      {usersQuery.isPending ? (
        <Skeleton variant="row" count={8} />
      ) : usersQuery.isError ? (
        <EmptyState
          icon={<Users size={20} />}
          title="Не удалось загрузить пользователей"
          description={humanizeError(usersQuery.error).message}
          action={
            <Button variant="primary" onClick={() => void usersQuery.refetch()}>
              Повторить
            </Button>
          }
        />
      ) : total === 0 ? (
        <EmptyState icon={<Users size={20} />} title="Пользователи не найдены" />
      ) : (
        <>
          <Table
            ariaLabel="Пользователи"
            columns={columns}
            rows={usersQuery.data?.items ?? []}
            getRowId={(user) => user.id}
          />
          <div className={styles['pagination']}>
            <Button size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Назад
            </Button>
            <span className="u-muted">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} из {total}
            </span>
            <Button size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Вперёд
            </Button>
          </div>
        </>
      )}

      {/* Приглашение по email: пароль задаёт сам пользователь (UC-USR-06). */}
      <InviteUserModal open={isCreateOpen} onClose={() => setCreateOpen(false)} />

      <ConfirmDialog
        open={revoking !== null}
        title="Отозвать приглашение?"
        description={
          revoking
            ? `Ссылка для ${revoking.email} перестанет работать, а учётная запись будет удалена. Email можно пригласить снова.`
            : ''
        }
        confirmLabel="Отозвать"
        isPending={revokeMutation.isPending}
        onClose={() => setRevoking(null)}
        onConfirm={() => {
          if (revoking?.invitationId) revokeMutation.mutate(revoking.invitationId);
        }}
      />

      <Modal
        open={editing !== null}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
        }}
        title={editing ? `Изменить ${editing.displayName}` : 'Новый пользователь'}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setCreateOpen(false);
                setEditing(null);
              }}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={updateMutation.isPending}
              onClick={() => {
                if (!editing) return;
                if (form.displayName.trim().length === 0) {
                  setFormError('Заполните имя');
                  return;
                }
                if (isAgent) {
                  updateMutation.mutate({
                    id: editing.id,
                    body: { displayName: form.displayName.trim() },
                  });
                  return;
                }
                if (form.email.trim().length === 0) {
                  setFormError('Заполните email');
                  return;
                }
                updateMutation.mutate({
                  id: editing.id,
                  body: {
                    email: form.email.trim(),
                    displayName: form.displayName.trim(),
                    role: form.role,
                    ...(form.password.length >= 8 ? { password: form.password } : {}),
                  },
                });
              }}
            >
              Сохранить
            </Button>
          </>
        }
      >
        <div className={styles['form']}>
          {editing ? (
            <UserAvatarEditor
              user={editing}
              onChanged={(updated) => {
                setEditing(updated);
                invalidate();
                toast.success('Аватар обновлён');
              }}
            />
          ) : null}
          <Input
            label="Логин"
            value={form.login}
            disabled
            hint="Логин изменить нельзя"
            onChange={(event) => setForm({ ...form, login: event.target.value })}
          />
          <Input
            label="Имя"
            value={form.displayName}
            onChange={(event) => setForm({ ...form, displayName: event.target.value })}
          />
          {isAgent ? (
            <p>
              Специализация фиксирована, сервер, LLM и установка меняются на странице
              «AI-агенты».
            </p>
          ) : (
            <>
              <Input
                label="Email"
                type="email"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />
              <Select
                label="Глобальная роль"
                options={ROLE_OPTIONS}
                value={form.role}
                onChange={(value) => setForm({ ...form, role: value })}
              />
              <Input
                label="Новый пароль (необязательно)"
                type="password"
                autoComplete="new-password"
                value={form.password}
                error={formError}
                hint="Оставьте пустым, чтобы не менять"
                onChange={(event) => setForm({ ...form, password: event.target.value })}
              />
            </>
          )}
          {formError ? (
            <p className={styles['formError']} role="alert">
              {formError}
            </p>
          ) : null}
        </div>
      </Modal>
    </Page>
  );
}
